import { ValidateFields } from 'rc-field-form/es/interface';
import React, { useCallback, useContext, useMemo } from 'react';
import { IItemForGetShortInfoWithStatus, UserRoles, ITotalPrice, EntityStatus } from 'types';
import AddNewItemBtn from './components/AddNewFieldButton';
import RestaurantsMultipleSelect from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import PriceField from './components/PriceField';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { AppContext, IAppContext } from 'appState/appContext';
import style from './RestaurantsField.module.scss';

interface IRestaurantsField {
	addPrices: (value: ITotalPrice[]) => void;
	removePrice: (value: string[]) => void;
	setRestaurantsForPrice: (value: string[]) => void;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
	price: string;
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	selectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	changePrice: (value: string) => void;
	revalidateForm: ValidateFields;
	isCreatedInPos: boolean;
}

const RestaurantsField: React.FC<IRestaurantsField> = ({
	restaurantsForPrice,
	price,
	totalPrices,
	selectedCustomer,
	setRestaurantsForPrice,
	removePrice,
	addPrices,
	setFetchedRestaurants,
	fetchedRestaurants,
	changePrice,
	revalidateForm,
	isCreatedInPos,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const disabledButton = !restaurantsForPrice.length || !price || isCreatedInPos;

	const restaurantsList = useMemo(() => {
		return totalPrices.map((el) => {
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			return (
				<SelectedItem
					key={el.restaurantId}
					label={[el.restaurantName, el.price]}
					onDelete={
						isCreatedInPos || !(hasAccess || accessibleRestaurantIds?.includes(el.restaurantId))
							? undefined
							: () => removePrice([el.restaurantId])
					}
				/>
			);
		});
	}, [totalPrices, loggedUser, isCreatedInPos, hasAccess, removePrice]);

	const handleAddPrice = useCallback(() => {
		const prices: ITotalPrice[] = restaurantsForPrice.map((el) => {
			const fetchedRestaurant = fetchedRestaurants.find((fetched) => fetched.id === el);
			return {
				restaurantId: el,
				restaurantName: fetchedRestaurant?.name || '',
				restaurantStatus: fetchedRestaurant?.status || EntityStatus.Active,
				price,
			};
		});
		prices.forEach((el) => {
			if (!totalPrices.some((total) => total.restaurantId === el.restaurantId)) {
				addPrices([el]);
				revalidateForm();
			}
		});
	}, [restaurantsForPrice, price, totalPrices, fetchedRestaurants, addPrices]);

	return (
		<div className={style.container} data-test="RestaurantsField">
			<div className={style.field}>
				<RestaurantsMultipleSelect
					selectedCustomer={selectedCustomer}
					restaurantsForPrice={restaurantsForPrice}
					totalPrices={totalPrices}
					setFetchedRestaurants={setFetchedRestaurants}
					setRestaurantsForPrice={setRestaurantsForPrice}
					removePrice={removePrice}
					isCreatedInPos={isCreatedInPos}
				/>
				<PriceField
					restaurantsForPrice={restaurantsForPrice}
					changePrice={changePrice}
					isCreatedInPos={isCreatedInPos}
				/>
				<AddNewItemBtn onClick={handleAddPrice} isDisabled={disabledButton} />
			</div>
			<div className={style.pricesList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsField);
